import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { 
  Building2, Phone, Mail, MapPin, Clock, Save, 
  Target, Eye, FileText, Loader2, CheckCircle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminSettings = () => {
  const [companyInfo, setCompanyInfo] = useState({
    company_name: '',
    tagline: '',
    phone: '',
    email: '',
    address: '',
    city: '',
    state: '',
    zip_code: '',
    office_hours: '',
    mission: '',
    vision: '',
    about_text: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchCompanyInfo();
  }, []);

  const fetchCompanyInfo = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`${API_URL}/api/admin/company-info`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCompanyInfo(response.data);
    } catch (error) {
      console.error('Error fetching company info:', error);
      if (error.response?.status === 404) {
        // No company info exists yet, use default values
        setCompanyInfo({
          company_name: 'Lehigh Human Support Services',
          tagline: 'Compassionate Care, Empowering Lives',
          phone: '(610) 555-0123',
          email: 'info@lehigh-hss.com',
          address: '123 Main Street',
          city: 'Allentown',
          state: 'PA',
          zip_code: '18101',
          office_hours: 'Monday - Friday: 8:00 AM - 5:00 PM',
          mission: 'To provide exceptional human services that empower individuals and families to achieve their full potential.',
          vision: 'A community where every individual has access to the support they need to thrive.',
          about_text: 'Lehigh Human Support Services has been serving the Lehigh Valley community for over 20 years.'
        });
      } else {
        setError('Failed to load company information');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setCompanyInfo(prev => ({ ...prev, [name]: value }));
    setSaved(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    try {
      const token = localStorage.getItem('adminToken');
      await axios.put(`${API_URL}/api/admin/company-info`, companyInfo, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (error) {
      console.error('Error saving company info:', error);
      setError('Failed to save company information. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-settings">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-3xl font-bold text-gray-900">Company Settings</h2>
          <p className="text-gray-600 mt-2">Update your company information and details</p>
        </div>
        
        <Button 
          onClick={handleSubmit}
          disabled={saving}
          className="bg-amber-500 hover:bg-amber-600"
          data-testid="save-settings-btn"
        >
          {saving ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Saving...
            </>
          ) : saved ? (
            <>
              <CheckCircle className="w-4 h-4 mr-2" />
              Saved!
            </>
          ) : (
            <>
              <Save className="w-4 h-4 mr-2" />
              Save Changes
            </>
          )}
        </Button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Information */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-amber-500" />
              Basic Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="company_name">Company Name</Label>
                <Input
                  id="company_name"
                  name="company_name"
                  value={companyInfo.company_name}
                  onChange={handleChange}
                  placeholder="Enter company name"
                  data-testid="input-company-name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="tagline">Tagline</Label>
                <Input
                  id="tagline"
                  name="tagline"
                  value={companyInfo.tagline}
                  onChange={handleChange}
                  placeholder="Enter company tagline"
                  data-testid="input-tagline"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Contact Information */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Phone className="w-5 h-5 text-amber-500" />
              Contact Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="phone">Phone Number</Label>
                <Input
                  id="phone"
                  name="phone"
                  value={companyInfo.phone}
                  onChange={handleChange}
                  placeholder="(555) 555-5555"
                  data-testid="input-phone"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email Address</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={companyInfo.email}
                  onChange={handleChange}
                  placeholder="info@company.com"
                  data-testid="input-email"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Address */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MapPin className="w-5 h-5 text-amber-500" />
              Address
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="address">Street Address</Label>
              <Input
                id="address"
                name="address"
                value={companyInfo.address}
                onChange={handleChange}
                placeholder="123 Main Street"
                data-testid="input-address"
              />
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="space-y-2 col-span-2">
                <Label htmlFor="city">City</Label>
                <Input
                  id="city"
                  name="city"
                  value={companyInfo.city}
                  onChange={handleChange}
                  placeholder="City"
                  data-testid="input-city"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="state">State</Label>
                <Input
                  id="state"
                  name="state"
                  value={companyInfo.state}
                  onChange={handleChange}
                  placeholder="PA"
                  data-testid="input-state"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="zip_code">ZIP Code</Label>
                <Input
                  id="zip_code"
                  name="zip_code"
                  value={companyInfo.zip_code}
                  onChange={handleChange}
                  placeholder="12345"
                  data-testid="input-zip"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Office Hours */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-amber-500" />
              Office Hours
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <Label htmlFor="office_hours">Hours of Operation</Label>
              <Input
                id="office_hours"
                name="office_hours"
                value={companyInfo.office_hours}
                onChange={handleChange}
                placeholder="Monday - Friday: 9:00 AM - 5:00 PM"
                data-testid="input-hours"
              />
            </div>
          </CardContent>
        </Card>

        {/* Mission & Vision */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Target className="w-5 h-5 text-amber-500" />
              Mission & Vision
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="mission">Mission Statement</Label>
              <Textarea
                id="mission"
                name="mission"
                value={companyInfo.mission}
                onChange={handleChange}
                placeholder="Enter your company's mission statement"
                rows={3}
                data-testid="input-mission"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="vision">Vision Statement</Label>
              <Textarea
                id="vision"
                name="vision"
                value={companyInfo.vision}
                onChange={handleChange}
                placeholder="Enter your company's vision statement"
                rows={3}
                data-testid="input-vision"
              />
            </div>
          </CardContent>
        </Card>

        {/* About Text */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-amber-500" />
              About Us Content
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <Label htmlFor="about_text">About Text</Label>
              <Textarea
                id="about_text"
                name="about_text"
                value={companyInfo.about_text}
                onChange={handleChange}
                placeholder="Enter content for the About Us section"
                rows={5}
                data-testid="input-about"
              />
            </div>
          </CardContent>
        </Card>

        {/* Submit Button (Mobile) */}
        <div className="sm:hidden">
          <Button 
            type="submit"
            disabled={saving}
            className="w-full bg-amber-500 hover:bg-amber-600"
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Save Changes
              </>
            )}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default AdminSettings;
